<template>
  <form
    v-on:submit.prevent="onSubmit"
    class="form-product">
    <input-text
      placeholder="Наименование"
      title="Наименование"
      v-bind:violations="nameInputErrors"
      v-bind:maxlength="200"
      v-letter-capital
      v-model="name" />
    <input-text
      placeholder="Производитель"
      v-bind:maxlength="200"
      title="Производитель"
      v-bind:violations="manufacturerInputErrors"
      v-letter-capital
      v-model="manufacturer" />
    <autocomplete-input
      item-text="name"
      item-value="id"
      label="Категория"
      v-bind:menu-props="{ maxWidth: 'min(80%, 586px)' }"
      v-bind:items="categoryOptions"
      v-bind:violations="categoryInputErrors"
      v-bind:clearable="true"
      v-bind:custom-item="categoryCustomItem"
      v-on:custom-item-click="onCreateCategory"
      v-model="category"
      attach />
    <autocomplete-input
      item-text="name"
      item-value="id"
      label="Единица измерения"
      v-bind:menu-props="{ maxWidth: 'min(80%, 586px)' }"
      v-bind:items="unitOptions"
      v-bind:violations="unitInputErrors"
      v-bind:clearable="true"
      v-bind:custom-item="unitCustomItem"
      v-on:custom-item-click="onCreateUnit"
      v-model="unit"
      attach />
    <input-number
      v-if="unitIsPack"
      min="0"
      title="Кол-во в упаковке"
      v-model="countInPack"
      without-native-controls />
    <autocomplete-input
      item-text="name"
      item-value="id"
      label="Поставщик"
      v-bind:menu-props="{ maxWidth: 'min(80%, 586px)' }"
      v-bind:items="vendorOptions"
      v-bind:violations="vendorInputErrors"
      v-bind:clearable="true"
      v-model="vendor"
      attach />
    <div class="form-product__buttons">
      <button
        class="form-product__button-cancel"
        type="button"
        v-on:click="onClose">Отмена</button>
      <button
        class="form-product__button-save"
        type="submit">
        Сохранить
      </button>
    </div>
  </form>
</template>

<script>
import InputText from '@/components/InputText.vue';
import AutocompleteInput from '@/components/AutocompleteInput.vue';
import InputNumber from '@/components/InputNumber.vue';
import { ProductRequest } from '@/storage/gateways/ProductGateway';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

/**
 * @description Название события подтверждения.
 * @type {string}
 */
const NAME_EVENT_SUBMIT = 'submit';

/**
 * @description Название события закрытия.
 * @type {string}
 */
const NAME_EVENT_CLOSE = 'close';

/**
 * @description Название события создания категории.
 * @type {string}
 */
const NAME_EVENT_CREATE_CATEGORY = 'create-category';

/**
 * @description Название события создания единицы измерения.
 * @type {string}
 */
const NAME_EVENT_CREATE_UNIT = 'create-unit';

/**
 * @description Форма товара.
 * @type {any}
 */
export default {
  name: 'FormVendor',
  components: {
    InputNumber,
    AutocompleteInput,
    InputText,
  },
  computed: {
    nameInputErrors: resolveInputErrors('name'),
    manufacturerInputErrors: resolveInputErrors('manufacturer'),
    categoryInputErrors: resolveInputErrors('category'),
    unitInputErrors: resolveInputErrors('unit'),
    vendorInputErrors: resolveInputErrors('vendor'),
    /**
     * @description Редактируется.
     * @returns {boolean}
     */
    isEdit() {
      return this.productId !== '';
    },
    /**
     * @description Единица измерения "упаковка".
     * @returns {boolean}
     */
    unitIsPack() {
      return this.unitOptions.find((item) => item.id === this.unit)?.isPack ?? false;
    },
    /**
     * @description Уникальный элемент ед. измерения.
     * @returns {{name: string, id: string}|null}
     */
    unitCustomItem() {
      return this.hasCreateUnit ? { name: 'Добавить единицу измерения', id: 'new' } : null;
    },
    /**
     * @description Уникальный элемент категорий.
     * @returns {{name: string, id: string}|null}
     */
    categoryCustomItem() {
      return this.hasCreateCategory ? { name: 'Добавить категорию', id: 'new' } : null;
    },
  },
  data() {
    return {
      name: this.initialName,
      manufacturer: this.initialManufacturer,
      category: this.initialCategory,
      unit: this.initialUnit,
      countInPack: this.initialCountInPack,
      vendor: this.initialVendor,
    };
  },
  methods: {
    /**
     * @description Обработать закрытие формы.
     * @returns {void}
     */
    onClose() {
      this.$emit(NAME_EVENT_CLOSE);
    },
    /**
     * @description Обработать создание категории.
     * @returns {void}
     */
    onCreateCategory() {
      this.$emit(NAME_EVENT_CREATE_CATEGORY);
      this.category = null;
    },
    /**
     * @description Обработать создание единицы измерения.
     * @returns {void}
     */
    onCreateUnit() {
      this.unit = '';
      this.$emit(NAME_EVENT_CREATE_UNIT);
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      const request = this.makeRequest();
      this.$emit(NAME_EVENT_SUBMIT, request);
    },
    /**
     * @description Создать запрос.
     * @returns {ProductRequest}
     */
    makeRequest() {
      return new ProductRequest(
        this.productId ?? null,
        this.name,
        this.manufacturer,
        this.category,
        this.unit,
        this.vendor,
        this.countInPack,
      );
    },
  },
  props: {
    /**
     * @description Идентификатор товара.
     */
    productId: { type: String, default: () => '' },
    /**
     * @description Начальное наименование.
     */
    initialName: { type: String, default: () => '' },
    /**
     * @description Начальное контактное лицо.
     */
    initialManufacturer: { type: String, default: () => '' },
    /**
     * @description Начальный телефон.
     */
    initialCategory: { type: String, default: () => '' },
    /**
     * @description Начальная почта.
     */
    initialUnit: { type: String, default: () => '' },
    /**
     * @description Начальный поставщик.
     */
    initialVendor: { type: String, default: () => '' },
    /**
     * @description Кол-во в упаковке.
     */
    initialCountInPack: { type: Number, default: () => 0 },
    /**
     * @description Нарушения ограничений.
     * @type {any}
     */
    inputErrors: { type: Object, required: true },
    /**
     * @description Опции категорий.
     * @type {Array}
     */
    categoryOptions: { required: true, type: Array },
    /**
     * @description Опции единиц измерения.
     * @type {Array}
     */
    unitOptions: { required: true, type: Array },
    /**
     * @description Опции поставщики.
     * @type {Array}
     */
    vendorOptions: { required: true, type: Array },
    /**
     * @description Может создавать единицы измерения.
     * @type {Boolean}
     */
    hasCreateUnit: { default: () => true, type: Boolean },
    /**
     * @description Может создавать единицы измерения.
     * @type {Boolean}
     */
    hasCreateCategory: { default: () => true, type: Boolean },
  },
  watch: {
    initialCategory(value) {
      this.category = value;
    },
    initialUnit(value) {
      this.unit = value;
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";
button {
  outline: none !important;
}
.colorItem:focus,
.product-form__icon__image-select > button:focus {
  border: 2px solid #c2cfe0;
  border-radius: 50%;
}
.form-product {
  position: relative;
  display: grid;
  gap: 0.75rem;
  padding-block: 20px;
  &__caption {
    font-weight: 600;
    font-size: 24px;
    line-height: 29px;
  }
  &__exit {
    position: absolute;
    right: -10px;
    top: 0px;
    background-image: url("@/assets/img/exit-black.svg");
    background-position: center;
    background-size: auto;
    background-repeat: no-repeat;
    background-color: #eaeff3;
    padding: 9px;
    border-radius: 2px;
    width: 32px;
    height: 32px;
    cursor: pointer;
    &:hover {
      background-color: transparent;
    }
  }
  &__form {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 20px;
    margin: 30px 0 0 0;
  }
  &__icon {
    &__caption {
      font-weight: 600;
      font-size: 16px;
      line-height: 19px;
      color: #192a3e;
      margin: 0 0 10px 0;
    }
    &__colors {
      flex: 1;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
    }
    &__color {
      min-width: 36px;
      width: 36px;
      height: 36px;
      min-height: 36px;
      border-radius: 50%;
      margin: 0 10px 10px 0;
      cursor: pointer;
      transition: border-width 0.1s;
      &.outline {
        border: 4px solid #c2cfe0;
      }
      &:hover {
        border: 4px solid #c2cfe0;
      }
    }
    &__image-select {
      position: relative;
      width: 86px;
      height: 86px;
      align-self: flex-end;
      margin-right: 40px;
      margin-bottom: 10px;
      &__upload {
        border-radius: 50%;
        width: 100%;
        height: 100%;
        cursor: pointer;
        background: #eaeff3;
        background-image: url("@/assets/img/camera.svg");
        background-repeat: no-repeat;
        background-position: center;
      }
      &__image {
        width: 100%;
        height: 100%;
      }
      &__close::after {
        cursor: pointer;
        font: normal normal bolder 25px/1 "Material Design Icons";
        display: block;
        position: absolute;
        transform: rotate(45deg);
        color: red;
        top: -7px;
        right: -7px;
        content: "\F0415";
      }
      &__image > img {
        border-radius: 50%;
        object-fit: cover;
        width: 100%;
        height: 100%;
      }
    }
    &__one-row {
      display: flex;
      flex-direction: row;
    }
    &__text {
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      color: var(--unfocus-color);
    }
  }
  &__buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
    max-width: 438px;
    width: 100%;
    margin: 30px 0 0 auto;
  }
  &__button {
    &-cancel {
      @include buttons.btn-transparent(100%, 3rem, 1rem);
    }
    &-save {
      @include buttons.btn-blue(100%, 3rem, 1rem);
    }
  }
}
.validate-error {
  padding: 3px;
  color: red;
  font-size: 0.8rem;
}
</style>
